import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  addTrainEnvPacks,
  createTrainEnv,
  deleteTrainEnv,
  extendTrainEnv,
  getAdminUsers,
  getCandidates,
  getTrainEnvs,
  type AdminCandidate,
  type AdminUser,
  type TrainEnv,
} from '../lib/api';
import { formatActiveWhen, timeInMittiLine } from '../lib/format-duration';
import { mittiGrantLine, packLabel, packStatusLine } from '../lib/mitti-copy';
import PackPills from './PackPills';

const PACKS: { id: string; name: string; help: string }[] = [
  {
    id: 'irm',
    name: 'IRM',
    help: 'Integrated Risk Management. Risks, exceptions, findings, and treatment, on the security spine.',
  },
  {
    id: 'apisec',
    name: 'API Security',
    help: 'API inventory, findings, and posture for the APIs a team runs.',
  },
  {
    id: 'lms',
    name: 'LMS',
    help: 'Learning management. Courses, enrolment, and progress inside Henoyo.',
  },
];



function errText(code: string | undefined, status: number): string {
  if (status === 0) return 'Network error — check your connection.';
  switch (code) {
    case 'invalid_user':
    case 'user_not_found':
      return 'Choose a learner who has signed in.';
    case 'invalid_packs':
      return 'Choose only IRM, API Security, or LMS.';
    case 'invalid_days':
      return 'Duration must be from 1 to 30 days.';
    case 'already_active':
      return 'This learner already has a Mitti. Extend it, or delete it first.';
    case 'capacity':
      return 'The Mitti limit is reached. Delete one or wait for one to expire.';
    case 'not_found':
      return 'That Mitti is gone.';
    case 'still_provisioning':
      return 'This Mitti is still being prepared.';
    case 'env_failed':
      return 'Preparation failed. Delete it and give a new Mitti.';
    case 'env_expired':
      return 'This Mitti has expired.';
    case 'rebuild_provision_failed':
      return 'Rebuild failed after the old Mitti was removed. Use Give a Mitti to create a new one.';
    case 'rate_limited':
      return 'Too many requests. Wait a moment.';
    case 'forbidden':
      return 'You do not have access to this.';
    default:
      return status ? `Could not do that (HTTP ${status}).` : 'Something went wrong.';
  }
}

function stateWord(state: string): string {
  switch (state) {
    case 'provisioning':
      return 'Being prepared';
    case 'ready':
      return 'Ready';
    case 'running':
      return 'Running';
    case 'stopped':
      return 'Stopped';
    case 'failed':
      return 'Failed';
    case 'expired':
      return 'Expired';
    default:
      return state;
  }
}

function badgeClass(state: string): string {
  if (state === 'running' || state === 'ready') {
    return 'bg-verdant-50 text-verdant-800 dark:bg-verdant-900/40 dark:text-verdant-200';
  }
  if (state === 'failed') return 'bg-terracotta-50 text-terracotta-800 dark:bg-terracotta-900/40 dark:text-terracotta-200';
  return 'bg-ink-100 text-ink-600 dark:bg-clay-raised dark:text-paper-200';
}

type Learner = { uid: string; email: string; displayName: string; kind: 'Employee' | 'Candidate' };

export default function AdminTrainEnvs() {
  const [learners, setLearners] = useState<Learner[]>([]);
  const [envs, setEnvs] = useState<TrainEnv[]>([]);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [uid, setUid] = useState('');
  const [packs, setPacks] = useState<string[]>([]);
  const [days, setDays] = useState(7);
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const [rowBusy, setRowBusy] = useState<string | null>(null);
  const [confirmSlug, setConfirmSlug] = useState<string | null>(null);
  const [rebuildSlug, setRebuildSlug] = useState<string | null>(null);
  const [addSlug, setAddSlug] = useState<string | null>(null);
  const [addPick, setAddPick] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    const [people, candidates, list] = await Promise.all([getAdminUsers(), getCandidates(), getTrainEnvs()]);
    if (people.status === 403 || candidates.status === 403 || list.status === 403) {
      setForbidden(true);
      setLoading(false);
      return;
    }
    if (!people.ok || !candidates.ok || !list.ok) {
      setLoadError('Could not load Mittis. Try again.');
      setLoading(false);
      return;
    }
    const employees: Learner[] = (people.data.users || []).map((u: AdminUser) => ({
      uid: u.uid,
      email: u.email,
      displayName: u.displayName || u.email,
      kind: 'Employee',
    }));
    const aspirants: Learner[] = (candidates.data.candidates || []).map((c: AdminCandidate) => ({
      uid: c.uid,
      email: c.email,
      displayName: c.displayName || c.email,
      kind: 'Candidate',
    }));
    setLearners([...employees, ...aspirants]);
    setEnvs(Array.isArray(list.data.envs) ? list.data.envs : []);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function togglePack(id: string) {
    setPacks((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  async function provision() {
    if (inflight.current) return;
    setFormError(null);
    if (!uid) {
      setFormError('Choose a learner.');
      return;
    }
    const duration = Math.round(Number(days));
    if (!Number.isInteger(duration) || duration < 1 || duration > 30) {
      setFormError('Duration must be from 1 to 30 days.');
      return;
    }
    inflight.current = true;
    setBusy(true);
    const res = await createTrainEnv({ uid, packs, days: duration }).finally(() => {
      inflight.current = false;
      setBusy(false);
    });
    if (res.status === 403) {
      setForbidden(true);
      return;
    }
    if (!res.ok) {
      setFormError(errText(res.data?.error, res.status));
      return;
    }
    setUid('');
    setPacks([]);
    setDays(7);
    await load();
  }

  async function extend(slug: string) {
    setRowBusy(slug);
    setFormError(null);
    const res = await extendTrainEnv(slug);
    setRowBusy(null);
    if (!res.ok) {
      setFormError(errText(res.data?.error, res.status));
      return;
    }
    await load();
  }

  async function remove(slug: string) {
    setRowBusy(slug);
    setFormError(null);
    const res = await deleteTrainEnv(slug);
    setRowBusy(null);
    setConfirmSlug(null);
    if (!res.ok) {
      setFormError(errText(res.data?.error, res.status));
      return;
    }
    await load();
  }

  async function rebuild(slug: string) {
    setRowBusy(slug);
    setFormError(null);
    const res = await api<{ ok: boolean; error?: string }>(
      'POST',
      `/api/admin/train-envs/${encodeURIComponent(slug)}/rebuild`,
    );
    setRowBusy(null);
    setRebuildSlug(null);
    // A Hosting gateway timeout has no error field; the rebuild keeps running, so just refresh.
    const gatewayCut = (res.status === 502 || res.status === 504) && !res.data?.error;
    if (!res.ok && !gatewayCut) {
      setFormError(errText(res.data?.error, res.status));
      return;
    }
    await load();
  }

  function openAdd(slug: string) {
    setAddSlug(slug);
    setAddPick([]);
    setConfirmSlug(null);
    setRebuildSlug(null);
  }

  function toggleAdd(id: string) {
    setAddPick((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }

  async function submitAdd(slug: string) {
    if (addPick.length === 0) return;
    setRowBusy(slug);
    setFormError(null);
    const res = await addTrainEnvPacks(slug, addPick);
    setRowBusy(null);
    if (!res.ok) {
      setFormError(errText(res.data?.error, res.status));
      return;
    }
    setAddSlug(null);
    setAddPick([]);
    await load();
  }

  if (forbidden) {
    return <p className="text-sm text-ink-500">You do not have access to Mittis.</p>;
  }

  const chosen = learners.find((l) => l.uid === uid);
  const grant = mittiGrantLine(chosen?.displayName || '', Math.round(Number(days)));

  const employees = learners.filter((l) => l.kind === 'Employee');
  const candidates = learners.filter((l) => l.kind === 'Candidate');

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-rule bg-paper-0 p-4 shadow-sm dark:border-clay-rule dark:bg-clay-surface">
        <h2 className="text-base font-semibold text-ink-900 dark:text-paper-50">Give a Mitti</h2>
        <p className="mt-1 text-sm text-ink-500 dark:text-paper-300">
          {grant}. Henoyo Core is always installed. Add a pack when they should practice that product. The Mitti
          stays off until they put in the Mehnat from their home page, then it sleeps when idle and is deleted at
          expiry. A start takes up to 3 minutes.
        </p>

        <label className="mt-4 block text-sm font-medium text-ink-800 dark:text-paper-100" htmlFor="train-env-learner">
          Learner
        </label>
        <select
          id="train-env-learner"
          value={uid}
          onChange={(e) => setUid(e.target.value)}
          className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-2 text-base text-ink-900 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50"
        >
          <option value="">Choose an employee or candidate</option>
          <optgroup label="Employees">
            {employees.map((l) => (
              <option key={l.uid} value={l.uid}>
                {l.displayName} · {l.email}
              </option>
            ))}
          </optgroup>
          <optgroup label="Candidates">
            {candidates.map((l) => (
              <option key={l.uid} value={l.uid}>
                {l.displayName} · {l.email}
              </option>
            ))}
          </optgroup>
        </select>
        {learners.length === 0 && !loading && (
          <p className="mt-1 text-xs text-ink-400">People appear here after they sign in.</p>
        )}

        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-ink-800 dark:text-paper-100">Packs</legend>
          <div className="mt-2 space-y-3">
            <label className="flex items-start gap-2 text-sm text-ink-700 dark:text-paper-200">
              <input type="checkbox" checked disabled className="mt-1" />
              <span>
                <span className="font-medium text-ink-900 dark:text-paper-50">Henoyo Core</span>
                <span className="mt-0.5 block text-xs text-ink-500 dark:text-paper-300">
                  Always installed. The Henoyo platform, including AI Studio. IRM and API Security also install the
                  security spine they depend on.
                </span>
              </span>
            </label>
            {PACKS.map((pack) => (
              <label key={pack.id} className="flex items-start gap-2 text-sm text-ink-700 dark:text-paper-200">
                <input
                  type="checkbox"
                  checked={packs.includes(pack.id)}
                  onChange={() => togglePack(pack.id)}
                  className="mt-1"
                />
                <span>
                  <span className="font-medium text-ink-900 dark:text-paper-50">{pack.name}</span>
                  <span className="mt-0.5 block text-xs text-ink-500 dark:text-paper-300">{pack.help}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <label className="mt-4 block text-sm font-medium text-ink-800 dark:text-paper-100" htmlFor="train-env-days">
          Duration
        </label>
        <div className="mt-1 flex items-center gap-2">
          <input
            id="train-env-days"
            type="number"
            min={1}
            max={30}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-24 rounded-lg border border-rule bg-paper-0 px-3 py-2 text-base text-ink-900 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50"
          />
          <span className="text-sm text-ink-500 dark:text-paper-300">days</span>
        </div>
        <p className="mt-1 text-xs text-ink-500 dark:text-paper-300">
          The Mitti is removed when this ends. The default is 7 days. You can add a week later.
        </p>

        {formError && <p className="mt-3 text-sm text-terracotta-700 dark:text-terracotta-200">{formError}</p>}

        <button
          type="button"
          onClick={() => void provision()}
          disabled={busy || !uid}
          className="mt-4 w-full rounded-lg bg-terracotta-500 px-4 py-2 text-sm font-medium text-white transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {busy ? 'Preparing…' : 'Give a Mitti'}
        </button>
      </section>

      <section>
        <h2 className="text-base font-semibold text-ink-900 dark:text-paper-50">Mittis</h2>
        <p className="mt-1 text-sm text-ink-500 dark:text-paper-300">
          Ready means the Mitti is prepared and powered off. The learner puts in the Mehnat from home. Stopped is
          the same, after it has already been used. A start takes up to 3 minutes.
        </p>
        {loading && <p className="mt-3 text-sm text-ink-400">Loading…</p>}
        {loadError && (
          <p className="mt-3 text-sm text-terracotta-700 dark:text-terracotta-200">
            {loadError}{' '}
            <button type="button" className="underline" onClick={() => void load()}>
              Retry
            </button>
          </p>
        )}
        {!loading && !loadError && envs.length === 0 && (
          <p className="mt-3 text-sm text-ink-400">No Mittis yet.</p>
        )}
        <ul className="mt-3 space-y-3">
          {envs.map((env) => {
            const missing = PACKS.filter((p) => !env.packs.includes(p.id));
            const canAdd =
              missing.length > 0 && env.state !== 'provisioning' && env.state !== 'failed' && env.state !== 'expired';
            const packsNote = packStatusLine(env.packsInstalling === true, (env.packsPending ?? []).map(packLabel));
            return (
              <li
                key={env.slug}
                className="rounded-xl border border-rule bg-paper-0 p-4 shadow-sm dark:border-clay-rule dark:bg-clay-surface"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink-900 dark:text-paper-50">
                      {env.displayName || env.email}
                    </p>
                    <p className="truncate text-sm text-ink-500 dark:text-paper-300">{env.email}</p>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${badgeClass(env.state)}`}>
                    {stateWord(env.state)}
                  </span>
                </div>
                <div className="mt-3">
                  <PackPills packs={env.packs} />
                </div>
                {packsNote && (
                  <p className="mt-2 text-sm text-ink-700 dark:text-paper-200" role="status">
                    {packsNote}
                  </p>
                )}
                <p className="mt-2 text-sm text-ink-800 dark:text-paper-100">
                  {timeInMittiLine(env.activeMinutes ?? 0, env.lastActiveAt)}
                </p>
                <dl className="mt-3 grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Last Mehnat</dt>
                    <dd className="mt-0.5 text-ink-800 dark:text-paper-100">{formatActiveWhen(env.lastStartedAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Expires</dt>
                    <dd className="mt-0.5 text-ink-800 dark:text-paper-100">{formatActiveWhen(env.expiresAt)}</dd>
                  </div>
                  <div className="sm:col-span-2">
                    <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">Host</dt>
                    <dd className="mt-0.5 break-all text-ink-800 dark:text-paper-100">{env.host || '—'}</dd>
                  </div>
                </dl>
                {addSlug === env.slug && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-ink-800 dark:text-paper-100">Add packs</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {missing.map((pack) => (
                        <label
                          key={pack.id}
                          className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                            addPick.includes(pack.id)
                              ? 'bg-terracotta-500 text-white'
                              : 'bg-paper-100 text-ink-500 dark:bg-clay-raised dark:text-paper-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5"
                            checked={addPick.includes(pack.id)}
                            onChange={() => toggleAdd(pack.id)}
                          />
                          {pack.name}
                        </label>
                      ))}
                    </div>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                      <button
                        type="button"
                        disabled={addPick.length === 0 || rowBusy === env.slug}
                        onClick={() => void submitAdd(env.slug)}
                        className="rounded-lg bg-terracotta-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {rowBusy === env.slug ? 'Adding…' : 'Add'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setAddSlug(null)}
                        className="rounded-lg px-3 py-1.5 text-sm text-ink-500 hover:text-ink-800 dark:hover:text-paper-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    disabled={rowBusy === env.slug || env.state === 'provisioning' || env.state === 'failed' || env.state === 'expired'}
                    onClick={() => void extend(env.slug)}
                    className="rounded-lg border border-rule px-3 py-1.5 text-sm font-medium text-ink-800 hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-clay-rule dark:text-paper-100 dark:hover:bg-clay-raised"
                  >
                    {rowBusy === env.slug ? 'Working…' : 'Extend (+7 days)'}
                  </button>
                  {confirmSlug === env.slug ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <span className="text-sm text-ink-600 dark:text-paper-200">
                        Delete this Mitti? The site and its login are removed.
                      </span>
                      <button
                        type="button"
                        onClick={() => void remove(env.slug)}
                        disabled={rowBusy === env.slug}
                        className="rounded-lg bg-terracotta-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-terracotta-600 disabled:opacity-50"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmSlug(null)}
                        className="rounded-lg px-3 py-1.5 text-sm text-ink-500 hover:text-ink-800 dark:hover:text-paper-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setRebuildSlug(null);
                        setConfirmSlug(env.slug);
                      }}
                      disabled={rowBusy === env.slug}
                      className="rounded-lg px-3 py-1.5 text-sm font-medium text-terracotta-700 hover:bg-terracotta-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-terracotta-200 dark:hover:bg-terracotta-900/30"
                    >
                      Delete
                    </button>
                  )}
                  {rebuildSlug === env.slug ? (
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                      <span className="text-sm text-ink-600 dark:text-paper-200">
                        Rebuild {env.displayName || env.email}&apos;s Mitti? Their current Mitti and anything they created
                        in it are deleted. A fresh Mitti on the newest image, with the same packs (
                        {env.packs.length > 0 ? env.packs.map(packLabel).join(', ') : 'Core only'}) and expiry, is prepared
                        in about 15–20 minutes, and they&apos;ll get the ready email.
                      </span>
                      <button
                        type="button"
                        onClick={() => void rebuild(env.slug)}
                        disabled={rowBusy === env.slug}
                        className="rounded-lg bg-terracotta-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-terracotta-600 disabled:opacity-50"
                      >
                        {rowBusy === env.slug ? 'Rebuilding…' : 'Rebuild'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setRebuildSlug(null)}
                        disabled={rowBusy === env.slug}
                        className="rounded-lg px-3 py-1.5 text-sm text-ink-500 hover:text-ink-800 disabled:opacity-50 dark:hover:text-paper-50"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      disabled={rowBusy === env.slug}
                      onClick={() => {
                        setConfirmSlug(null);
                        setAddSlug(null);
                        setRebuildSlug(env.slug);
                      }}
                      className="rounded-lg border border-rule px-3 py-1.5 text-sm font-medium text-ink-800 hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-clay-rule dark:text-paper-100 dark:hover:bg-clay-raised"
                    >
                      Rebuild
                    </button>
                  )}
                  {canAdd && addSlug !== env.slug && (
                    <button
                      type="button"
                      disabled={rowBusy === env.slug}
                      onClick={() => openAdd(env.slug)}
                      className="rounded-lg border border-rule px-3 py-1.5 text-sm font-medium text-ink-800 hover:bg-paper-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-clay-rule dark:text-paper-100 dark:hover:bg-clay-raised"
                    >
                      Add packs
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
